// frontend/app/components/lessonPlans/teachsense/TeachSenseMastery.js
//
// Per-concept mastery in the Loop's four bands.
//
// Two things here are the whole point of the section and easy to mistake for
// detail worth trimming.
//
// `n` travels with every bar. A band computed from three answers and a band
// computed from thirty are the same picture without it, and a teacher acts on
// the picture. The evidence count leads, because that is the denominator the
// band was actually computed from; the student count follows, because five
// answers from one student are less information than five from five.
//
// A concept with `mastery === null` has no evidence at all, and is drawn as a
// dashed empty track with "Not enough evidence yet" — never as a 0% bar. Zero
// means "asked, and nobody has it"; null means "nobody has been asked". Those
// call for opposite responses, so they must never share a picture.

import React from "react";
import { StyleSheet, Text, View } from "react-native";

import { tint } from "../lessonPlanTheme";
import { Pill } from "../blocks/LessonBlockShell";
import { Section } from "./TeachSenseSection";
import { BAND_KEYS, bandForScore, bandVisual, masteryPercent } from "./masteryBands";
import { countLabel, fill } from "./teachsenseText";

const TRACK_MAX = 360; // a mastery bar is NEVER wider than this, at any viewport

/**
 * How the class is spread across the four bands, low to high. Segments are
 * shares of the students counted, not of the roster, so a bar always fills its
 * track and the eye compares shape rather than length.
 */
const DistributionBar = ({ distribution, conceptKey, colors, isDarkMode, t }) => {
  // Named `count`, not `value`: reanimated warns about a `.value` read inside
  // an inline style, mistaking a plain number for a shared value.
  const segments = BAND_KEYS.map((key) => ({
    key,
    count: Math.max(0, Number(distribution?.[key]) || 0),
  })).filter((segment) => segment.count > 0);
  const total = segments.reduce((sum, segment) => sum + segment.count, 0);

  return (
    <View
      testID={`teachsense-track-${conceptKey}`}
      style={[
        styles.track,
        { backgroundColor: colors.subtleBg, borderColor: colors.border },
        total ? null : styles.trackEmpty,
      ]}
    >
      {total
        ? segments.map((segment) => {
            const band = bandVisual(segment.key, colors, t);
            return (
              <View
                key={segment.key}
                testID={`teachsense-band-${conceptKey}-${segment.key}`}
                style={[
                  styles.segment,
                  { flexGrow: segment.count, backgroundColor: band.accent },
                ]}
              />
            );
          })
        : null}
    </View>
  );
};

const ConceptRow = ({ concept, colors, isDarkMode, divided, t }) => {
  const conceptKey = String(concept?.concept_key || "");
  const label = String(concept?.label || conceptKey || t("Untitled concept"));
  const pct = masteryPercent(concept?.mastery);
  const hasMastery = pct !== null;
  const band = hasMastery
    ? bandVisual(concept?.band || bandForScore(concept?.mastery), colors, t)
    : null;

  const evidence = Math.max(0, Number(concept?.evidence_count) || 0);
  const students = Math.max(0, Number(concept?.student_count) || 0);
  const sample = `${countLabel(
    evidence,
    "piece of evidence",
    "pieces of evidence",
    t
  )} · ${countLabel(students, "student", "students", t)}`;

  const accessibilityLabel = hasMastery
    ? `${label}: ${band.label}, ${pct}%, ${sample}`
    : `${label}: ${t("Not enough evidence yet")}`;

  return (
    <View
      testID={`teachsense-concept-${conceptKey}`}
      accessibilityLabel={accessibilityLabel}
      style={[
        styles.row,
        divided ? { borderTopWidth: 1, borderTopColor: colors.border } : null,
      ]}
    >
      <View style={styles.head}>
        <Text style={[styles.label, { color: colors.textPrimary }]} numberOfLines={2}>
          {label}
        </Text>
        {hasMastery ? (
          <Text style={[styles.pct, { color: band.accent }]}>{`${pct}%`}</Text>
        ) : null}
      </View>

      {hasMastery ? (
        <>
          <View style={styles.barRow}>
            <DistributionBar
              distribution={concept?.distribution}
              conceptKey={conceptKey}
              colors={colors}
              isDarkMode={isDarkMode}
              t={t}
            />
            <Text style={[styles.sampleSize, { color: colors.textSecondary }]}>
              {fill(t("n = {n}"), { n: evidence })}
            </Text>
          </View>
          <View style={styles.meta}>
            <Pill
              colors={colors}
              label={band.label}
              accent={band.accent}
              background={tint(band.hex, isDarkMode, 1.4)}
            />
            <Text style={[styles.sample, { color: colors.textMuted }]}>{sample}</Text>
          </View>
        </>
      ) : (
        <>
          <View
            testID={`teachsense-track-${conceptKey}`}
            style={[
              styles.track,
              styles.trackEmpty,
              { backgroundColor: colors.subtleBg, borderColor: colors.border },
            ]}
          />
          <Text style={[styles.sample, styles.noEvidence, { color: colors.textMuted }]}>
            {t("Not enough evidence yet")}
          </Text>
        </>
      )}
    </View>
  );
};

const TeachSenseMastery = ({
  concepts = null,
  colors,
  isDarkMode = false,
  isNarrow = false,
  t,
}) => {
  const translate = t || ((value) => value);
  const entries = Array.isArray(concepts) ? concepts : [];

  return (
    <Section
      testID="teachsense-mastery"
      colors={colors}
      isNarrow={isNarrow}
      icon="chart-donut"
      accent={colors.accentPrimary}
      title={translate("Mastery by concept")}
      subtitle={translate(
        "Bands are computed from every kind of evidence, not from quiz answers alone."
      )}
      count={entries.length || null}
      isEmpty={entries.length === 0}
      empty={translate("This lesson has no tagged concepts yet.")}
    >
      {entries.map((concept, index) => (
        <ConceptRow
          key={concept.concept_key || `concept-${index}`}
          concept={concept}
          colors={colors}
          isDarkMode={isDarkMode}
          divided={index > 0}
          t={translate}
        />
      ))}
    </Section>
  );
};

const styles = StyleSheet.create({
  row: { paddingVertical: 12 },
  head: { flexDirection: "row", alignItems: "flex-end", marginBottom: 6 },
  label: { flex: 1, fontSize: 14, fontWeight: "600", lineHeight: 19 },
  pct: {
    fontSize: 17,
    fontWeight: "800",
    lineHeight: 22,
    marginLeft: 8,
    fontVariant: ["tabular-nums"],
  },

  barRow: { flexDirection: "row", alignItems: "center", gap: 10 },
  track: {
    height: 10,
    borderRadius: 999,
    borderWidth: 1,
    overflow: "hidden",
    flexDirection: "row",
    flex: 1,
    maxWidth: TRACK_MAX,
  },
  trackEmpty: { borderStyle: "dashed", opacity: 0.7 },
  segment: { flexBasis: 0, minWidth: 2 },
  sampleSize: { fontSize: 11, fontWeight: "800", fontVariant: ["tabular-nums"] },

  meta: { flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 8, marginTop: 8 },
  sample: { fontSize: 11, fontWeight: "500", lineHeight: 15 },
  noEvidence: { fontStyle: "italic", marginTop: 8 },
});

export default TeachSenseMastery;
